/**
 * Image Compression Utility for Web
 * Compresses images to 85% quality, max 1920x1920
 */

class ImageCompression {
    /**
     * Compress image
     * @param {File} file - Original image file
     * @param {Object} options - Compression options
     * @returns {Promise<File>} Compressed image file
     */
    static async compressImage(file, options = {}) {
      const {
        quality = 0.85,
        maxWidth = 1920,
        maxHeight = 1920,
        format = 'image/jpeg'
      } = options;
  
      return new Promise((resolve, reject) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          reject(new Error('File is not an image'));
          return;
        }
  
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const img = new Image();
          
          img.onload = () => {
            try {
              // Calculate new dimensions
              let width = img.width;
              let height = img.height;
  
              if (width > maxWidth || height > maxHeight) {
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                width = width * ratio;
                height = height * ratio;
              }
  
              // Create canvas
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
  
              // Draw and compress
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, width, height);
  
              // Convert to blob
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    reject(new Error('Compression failed'));
                    return;
                  }
  
                  // Create new File object
                  const compressedFile = new File(
                    [blob],
                    file.name.replace(/\.[^/.]+$/, '.jpg'), // Change extension to .jpg
                    {
                      type: format,
                      lastModified: Date.now()
                    }
                  );
  
                  resolve(compressedFile);
                },
                format,
                quality
              );
            } catch (error) {
              reject(error);
            }
          };
  
          img.onerror = () => {
            reject(new Error('Failed to load image'));
            
          };
  
          img.src = e.target.result;
        };
  
        reader.onerror = () => {
          reject(new Error('Failed to read file'));
        };
  
        reader.readAsDataURL(file);
      });
    }
  
    /**
     * Check if file is an image
     */
    static isImage(file) {
      return file.type.startsWith('image/');
    }
  
    /**
     * Get file size in MB
     */
    static getFileSizeMB(file) {
      return file.size / 1024 / 1024;
    }
  
    /**
     * Format file size for display
     */
    static formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    }
  }
  
  export default ImageCompression;
  
  
  
  